import { useCallback, useEffect, useRef, useState } from 'react'
import type { ITextFieldMainAction } from 'shared/ui/TextField'

import coreStyles from 'shared/ui/TextField/styles.module.scss'

type ITextFieldHighlightLimit = {
  limit: number
  onChange?: (active: boolean) => void
}

const CHARACTER_DISPLAY_TIMEOUT = 500
export const TEXT_FIELD_LIMIT_WARNING_MS = 3_000

export const makeTextFieldHighlightLimit: (
  action: ITextFieldHighlightLimit
) => ITextFieldMainAction = (action) => {
  const useAction: ITextFieldMainAction = (control, children) => {
    const [active, setActive] = useState(() => (control.value?.length ?? 0) >= action.limit)
    const characterTimer = useRef<number>(-1)
    const warningTimer = useRef<number>(-1)
    const controlRef = useRef(control)

    useEffect(() => {
      controlRef.current = control
    }, [control])

    const deactivate = useCallback(() => {
      controlRef.current.clearClassNames()
      window.clearTimeout(warningTimer.current)
      setActive(false)
    }, [])

    const trimIfNeeded = useCallback(() => {
      const inputToUpdate = controlRef.current.inputRef?.current
      if (!inputToUpdate) return

      const currentValue = inputToUpdate.value
      if (currentValue.length > action.limit) {
        const trimmedValue = currentValue.slice(0, action.limit)

        if (controlRef.current.setValue) {
          controlRef.current.setValue(trimmedValue)
        } else {
          inputToUpdate.value = trimmedValue
          inputToUpdate.dispatchEvent(new Event('input', { bubbles: true }))
        }
        inputToUpdate.setSelectionRange(action.limit, action.limit)
      }
      characterTimer.current = -1
    }, [action.limit])

    const activate = useCallback(() => {
      controlRef.current.setClassNames(coreStyles.textFieldLabel_warning)
      setActive(true)

      window.clearTimeout(warningTimer.current)

      warningTimer.current = window.setTimeout(() => {
        const currentLength = controlRef.current.value?.length ?? 0
        if (currentLength >= action.limit) {
          controlRef.current.clearClassNames()
          setActive(false)
        }
      }, TEXT_FIELD_LIMIT_WARNING_MS)
    }, [action.limit])

    useEffect(() => action.onChange?.(active), [active, action.onChange])

    useEffect(() => {
      const currentLength = control.value?.length ?? 0
      if (currentLength >= action.limit) {
        activate()
      } else {
        deactivate()
      }
    }, [control.value, action.limit, activate, deactivate])

    useEffect(() => {
      const input = controlRef.current.inputRef?.current

      if (!input) return

      input.removeAttribute('maxlength')

      const handleBeforeInput = (event: InputEvent) => {
        const inputElement = event.target as HTMLInputElement
        const currentLength = inputElement.value.length
        const isPaste =
          event.inputType === 'insertFromPaste' || event.inputType === 'insertFromDrop'

        if (isPaste) {
          return
        }

        if (currentLength >= action.limit && event.data) {
          activate()
          if (characterTimer.current !== -1) {
            window.clearTimeout(characterTimer.current)
          }

          characterTimer.current = window.setTimeout(trimIfNeeded, CHARACTER_DISPLAY_TIMEOUT)
        }
      }

      const handleInput = () => {
        setTimeout(() => {
          const inputToCheck = controlRef.current.inputRef?.current
          if (!inputToCheck) return

          const finalValue = inputToCheck.value
          const finalLength = finalValue.length

          if (finalLength > action.limit) {
            window.clearTimeout(characterTimer.current)

            characterTimer.current = window.setTimeout(trimIfNeeded, CHARACTER_DISPLAY_TIMEOUT)
          } else if (finalLength <= action.limit) {
            window.clearTimeout(characterTimer.current)
            characterTimer.current = -1
          }
        }, 0)
      }

      const handleBlur = () => {
        if (characterTimer.current !== -1) {
          window.clearTimeout(characterTimer.current)
          characterTimer.current = -1
          trimIfNeeded()
        }
        deactivate()
      }

      input.addEventListener('beforeinput', handleBeforeInput)
      input.addEventListener('input', handleInput)
      input.addEventListener('blur', handleBlur)

      return () => {
        input.removeEventListener('beforeinput', handleBeforeInput)
        input.removeEventListener('input', handleInput)
        input.removeEventListener('blur', handleBlur)
        if (characterTimer.current !== -1) {
          window.clearTimeout(characterTimer.current)
          characterTimer.current = -1
          trimIfNeeded()
        }
        deactivate()
      }
    }, [action.limit, activate, deactivate, trimIfNeeded])

    return children
  }

  return useAction
}
